import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/store/session';

/**
 * Character Active Special Skill Aura FX:
 *   - Luna (luna): Lunar Moonlight Halo Aura
 *   - Thor (thor): Electric Lightning Arc Flares
 *   - Rocky (rocky): Gold Ore Radar Sonar Waves
 *   - Sakura (sakura): Blossom Kyat Petal Shimmer
 *   - Jin (jin) / Mira (mira): Flame & Speed Radiance
 */
export function CharacterAuraFX() {
  const explorer = useSession((s) => s.characters.explorer);
  const heroKey = explorer?.key ?? '';

  if (!heroKey) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[6] overflow-hidden select-none" aria-hidden>
      {/* 1. Luna: Lunar Moonlight Halo */}
      {heroKey === 'luna' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="h-64 w-64 rounded-full bg-indigo-500/15 blur-xl animate-pulse" />
          <span className="absolute left-[20%] top-[40%] animate-bounce text-indigo-200">
            <Icon name="moon" size={14} />
          </span>
          <span className="absolute right-[22%] top-[35%] animate-pulse text-indigo-200">
            <Icon name="sparkle" size={11} />
          </span>
        </div>
      )}

      {/* 2. Thor: Electric Lightning Arc Flares */}
      {heroKey === 'thor' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="h-56 w-56 rounded-full border border-cyan-400/30 blur-sm animate-ping" />
          <span className="absolute left-[28%] top-[42%] animate-pulse text-cyan-200">
            <Icon name="energy" size={14} />
          </span>
          <span className="absolute right-[30%] bottom-[38%] animate-ping text-cyan-200">
            <Icon name="energy" size={11} />
          </span>
        </div>
      )}

      {/* 3. Rocky: Gold Ore Radar Sonar Waves */}
      {heroKey === 'rocky' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="h-72 w-72 rounded-full border border-amber-400/25 animate-[ping_3.2s_infinite]" />
          <span className="absolute left-[25%] bottom-[30%] animate-pulse text-amber-200">
            <Icon name="radar" size={13} />
          </span>
          <span className="absolute right-[25%] top-[28%] animate-pulse text-amber-200">
            <Icon name="rock" size={13} />
          </span>
        </div>
      )}

      {/* 4. Sakura: Blossom Kyat Petal Shimmer */}
      {heroKey === 'sakura' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="h-60 w-60 rounded-full bg-pink-500/15 blur-lg animate-pulse" />
          <span className="absolute left-[22%] top-[30%] animate-bounce text-pink-300">
            <Icon name="sparkle" size={12} />
          </span>
          <span className="absolute right-[20%] bottom-[35%] animate-pulse text-pink-300">
            <Icon name="sparkle" size={11} />
          </span>
        </div>
      )}

      {/* 5. Jin / Mira: Flame & Speed Radiance
             The flame was a bouncing 🔥, sitting in open air in the middle of the
             playfield — the one emoji a player stares at for a whole run. Drawn
             now, and tinted with the torch so it belongs to the cave's light. */}
      {(heroKey === 'jin' || heroKey === 'mira') && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="h-52 w-52 rounded-full bg-amber-500/10 blur-md animate-pulse" />
          <span className="absolute left-[26%] bottom-[40%] animate-bounce text-[var(--torch-warm)]">
            <Icon name="flame" size={13} />
          </span>
        </div>
      )}
    </div>
  );
}
